import uuid from 'uuid-random';
import { startOfDay } from 'date-fns';
import {
  map, is, hasPath, includes,
} from 'ramda';
import dateIsAfterDate from '~/lib/validators/dateIsAfterDate';
import i18n from '~/i18n';

import {
  multipleEmailsValidator,
  dateInFutureValidator,
  expenseDateValidator,
  dateIsAfterDateValidator,
  validatePass,
  validatePassConfirmation,
} from '~/lib/validators/formValidators';

import {
  countryCodeIsNot,
  countryCodeIs,
  defaultInvoiceKind,
  buyerAndSellerCountryIsEq,
  defaultInvoiceNumber,
} from '~/lib/formConfigHelpers';

import {
  BANK_TRANSFER,
  COUNTRY_CODES,
  CREDIT_CARD,
  CURRENCIES,
  INVOICE_KIND_ADVANCE,
  INVOICE_KIND_NO_VAT,
  INVOICE_KIND_PROFORMA,
  INVOICE_KIND_VAT_DUTY_REVERSED,
  INVOICE_KIND_VAT_MARGIN,
  INVOICE_KIND_WITH_VAT,
  LANGUAGES,
} from './constants';

const startOfTodayTimestamp = startOfDay(new Date()).getTime();

// TODO: consider to move this value to defaultValue function
// in get*name*FormConfig: FO-912
const defaultValues = {
  country_code: COUNTRY_CODES[i18n.locale.toUpperCase()],
  currency: CURRENCIES[i18n.locale.toUpperCase()],
  delivery_on: null,
  design: 4,
  due_in: '14',
  due_on_custom: startOfTodayTimestamp,
  issued_on: startOfTodayTimestamp,
  kind: defaultInvoiceKind(COUNTRY_CODES[i18n.locale.toUpperCase()]),
  language: LANGUAGES[i18n.locale.toUpperCase()],
  means_of_payment: 'bank_transfer',
  rounding_type: 'none',
  show_qr_code: undefined,
  tax_point_on: startOfTodayTimestamp,
  vat_calculation: 'vat_inclusive',
  seller_bank_account_show_iban: countryCodeIs(i18n.locale.toUpperCase(), [COUNTRY_CODES.RS, COUNTRY_CODES.NL]),
  lines: [
    {
      description: '',
      identifier: uuid(),
      position: 0,
      price: 0,
      quantity: 1,
      unit_type: null,
      vat_rate: 0,
      vat_rate_kind: 'standard',
      discount_amount: 0,
      discount_kind: 'percentage',
      is_discount_line: null,
      _destroy: false,
    },
  ],
};

export const getInvoiceFormConfig = state => ({
  dueOnIsCustom: false,
  kind: {
    validations: [
      {
        required: true,
        message: 'kind_required',
      },
    ],
    defaultValue: () => defaultValues.kind,
  },
  number: {
    validations: [
      {
        required: true,
        message: 'number_required',
      },
    ],
    defaultValue: () => {
      if (state && state.subscription.next_invoice_number) {
        return state.subscription.next_invoice_number;
      }
      return defaultInvoiceNumber();
    },
  },
  registration_number: {
    defaultValue: () => {
      if (state && state.subscription.next_invoice_number) {
        return state.subscription.next_invoice_number;
      }
      return defaultInvoiceNumber();
    },
    visibilityConditions: [
      () => countryCodeIsNot(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS, COUNTRY_CODES.GB, COUNTRY_CODES.NL])
        && [INVOICE_KIND_WITH_VAT, INVOICE_KIND_VAT_DUTY_REVERSED].includes(state.invoiceForm.kind),
    ],
  },
  issued_on: {
    validations: [
      {
        required: true,
        message: 'date_required',
      },
      {
        type: 'date',
        message: 'date_invalid',
      },
    ],
    defaultValue: () => defaultValues.issued_on,
  },
  tax_point_on: {
    validations: [
      {
        required: true,
        message: 'date_required',
      },
      {
        type: 'date',
        message: 'date_invalid',
      },
      {
        validator: (rule, value, callback) => {
          dateIsAfterDateValidator(rule, value, state, callback);
        },
      },
    ],
    visibilityConditions: [
      () => (state.invoiceForm.kind !== INVOICE_KIND_NO_VAT
        && state.invoiceForm.kind !== INVOICE_KIND_ADVANCE
        && state.invoiceForm.kind !== INVOICE_KIND_PROFORMA
        && state.invoiceForm.kind !== INVOICE_KIND_VAT_MARGIN)
        && countryCodeIsNot(state, [COUNTRY_CODES.SK, COUNTRY_CODES.EN, COUNTRY_CODES.RS, COUNTRY_CODES.NL, COUNTRY_CODES.GB]),
    ],
    defaultValue: () => {
      if (state) {
        const { issued_on: issued, tax_point_on: taxPoint } = state;
        if (dateIsAfterDate(taxPoint, issued)) {
          return taxPoint;
        }
        return issued;
      }
      return defaultValues.tax_point_on;
    },
  },
  delivery_on: {
    validations: [
      {
        type: 'date',
        message: 'date_invalid',
      },
    ],
    defaultValue: () => defaultValues.delivery_on,
    visibilityConditions: [
      () => countryCodeIsNot(state, COUNTRY_CODES.SK),
    ],
  },
  due_in: {
    validations: [
      {
        required: true,
        message: 'date_required',
      },
    ],
    visibilityConditions: [() => state.invoiceForm.due_in !== 'custom'],
    defaultValue: () => defaultValues.due_in,
  },
  due_on_custom: {
    validations: [
      {
        required: true,
        message: 'date_required',
      },
      {
        type: 'date',
        message: 'date_invalid',
      },
    ],
    visibilityConditions: [() => state.invoiceForm.due_in === 'custom'],
    defaultValue: () => defaultValues.due_on_custom,
  },
  means_of_payment: {
    validations: [
      {
        required: true,
        message: 'means_of_payment_required',
      },
    ],
    defaultValue: () => defaultValues.means_of_payment,
  },
  issued_by: {
    defaultValue: () => '',
  },
  reference: {
    defaultValue: () => '',
    visibilityConditions: [
      () => countryCodeIs(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS, COUNTRY_CODES.PL, COUNTRY_CODES.NL, COUNTRY_CODES.GB]),
    ],
  },
  specific_symbol: {
    validations: [
      {
        pattern: /^[0-9]{0,10}$/,
        message: 'specific_symbol_wrong_format',
      },
    ],
    visibilityConditions: [
      () => state.invoiceForm.means_of_payment === BANK_TRANSFER && countryCodeIsNot(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS, COUNTRY_CODES.PL, COUNTRY_CODES.NL, COUNTRY_CODES.GB]),
    ],
    defaultValue: () => '',
  },
  payment_symbol: {
    validations: [
      {
        pattern: /^[0-9]{0,10}$/,
        message: 'payment_symbol_wrong_format',
      },
    ],
    visibilityConditions: [
      () => state.invoiceForm.means_of_payment === BANK_TRANSFER && countryCodeIsNot(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS, COUNTRY_CODES.PL, COUNTRY_CODES.NL, COUNTRY_CODES.GB]),
    ],
    defaultValue: () => '',
  },
  constant_symbol: {
    validations: [
      {
        pattern: /^[0-9]{0,4}$/,
        message: 'constant_symbol_wrong_format',
      },
    ],
    visibilityConditions: [
      () => state.invoiceForm.means_of_payment === BANK_TRANSFER && countryCodeIsNot(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS, COUNTRY_CODES.PL, COUNTRY_CODES.NL, COUNTRY_CODES.GB]),
    ],
    defaultValue: () => '',
  },
  vat_calculation: {
    defaultValue: () => defaultValues.vat_calculation,
    visibilityConditions: [
      () => state.invoiceForm.kind !== INVOICE_KIND_NO_VAT
        && state.invoiceForm.kind !== INVOICE_KIND_PROFORMA
        && state.invoiceForm.kind !== INVOICE_KIND_VAT_MARGIN
        && state.invoiceForm.kind !== INVOICE_KIND_VAT_DUTY_REVERSED,
    ],
  },
  currency: {
    defaultValue: () => defaultValues.currency.toUpperCase(),
  },
  vat_totals_currency_conversion: {
    visibilityConditions: [
      () => state.invoiceForm.currency !== defaultValues.currency
        && countryCodeIs(state, COUNTRY_CODES.CS),
    ],
    defaultValue: () => false,
  },
  conversion_rate_message: {
    defaultValue: () => '',
  },
  rounding_type: {
    defaultValue: () => defaultValues.rounding_type,
  },
  language: {
    defaultValue: () => defaultValues.language,
  },
  logo: {
    defaultValue: () => '',
  },
  stamp: {
    defaultValue: () => '',
  },
  color: {
    defaultValue: () => '#2980b9',
    disableConditions: [
      () => state.invoiceForm.design !== 4,
    ],
  },
  design: {
    defaultValue: () => defaultValues.design,
  },
  buyer_history_list: {
    defaultValue: () => [],
  },
  buyer_name: {
    defaultValue: () => '',
  },
  buyer_name_placeholder: {
    visibilityConditions: [
      () => countryCodeIs(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS, COUNTRY_CODES.PL]),
    ],
  },
  buyer_tax_number_placeholder: {
    visibilityConditions: [
      () => countryCodeIs(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS]),
    ],
  },
  buyer_company_number: {
    defaultValue: () => '',
    visibilityConditions: [
      () => countryCodeIsNot(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS]),
    ],
  },
  buyer_tax_number: {
    defaultValue: () => '',
  },
  buyer_vat_number: {
    visibilityConditions: [
      () => state.invoiceForm.buyer_address_country_code === COUNTRY_CODES.SK,
    ],
    defaultValue: () => '',
  },
  buyer_address_street: {
    defaultValue: () => '',
  },
  buyer_address_city: {
    defaultValue: () => '',
  },
  buyer_address_postcode: {
    defaultValue: () => '',
  },
  buyer_address_country_code: {
    defaultValue: () => defaultValues.country_code,
  },
  buyer_address_subdivision: {
    defaultValue: () => defaultValues.subdivision,
  },
  buyer_email: {
    validations: [
      { validator: multipleEmailsValidator },
      {
        validator: (rule, value, callback) => {
          if (state.recurring_auto_submit && !value) {
            callback(new Error('email_required_for_recurring'));
          }
          callback();
        },
      },
    ],
    defaultValue: () => '',
  },
  buyer_phone: {
    defaultValue: () => '',
    validations: [
      {
        validator: (rule, value, callback) => {
          if (!!value && !state.phoneValidations.buyer && state.phoneManuallyEdited.buyer) {
            callback(new Error('phone_is_not_valid'));
          } else {
            callback();
          }
        },
        trigger: 'blur',
      },
    ],
  },
  buyer_web: {
    defaultValue: () => '',
  },
  seller_company_number: {
    defaultValue: () => '',
    visibilityConditions: [
      () => countryCodeIsNot(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS]),
    ],
  },
  seller_name: {
    defaultValue: () => '',
  },
  seller_name_placeholder: {
    visibilityConditions: [
      () => countryCodeIs(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS, COUNTRY_CODES.PL]),
    ],
  },
  seller_information: {
    defaultValue: () => '',
  },
  seller_tax_number_placeholder: {
    visibilityConditions: [
      () => countryCodeIs(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS]),
    ],
  },
  seller_tax_number: {
    defaultValue: () => '',
  },
  seller_vat_number: {
    visibilityConditions: [
      () => state.invoiceForm.seller_address_country_code === COUNTRY_CODES.SK,
    ],
    defaultValue: () => '',
  },
  seller_address_street: {
    defaultValue: () => '',
  },
  seller_address_city: {
    defaultValue: () => '',
  },
  seller_address_postcode: {
    defaultValue: () => '',
  },
  seller_address_country_code: {
    defaultValue: () => defaultValues.country_code,
  },
  seller_address_subdivision: {
    defaultValue: () => defaultValues.subdivision,
  },
  seller_bank_account_number: {
    defaultValue: () => '',
    visibilityConditions: [
      () => countryCodeIsNot(state, [COUNTRY_CODES.RS, COUNTRY_CODES.NL]),
    ],
  },
  seller_bank_account_show_iban: {
    defaultValue: () => defaultValues.seller_bank_account_show_iban,
    visibilityConditions: [
      () => countryCodeIsNot(state, [COUNTRY_CODES.RS, COUNTRY_CODES.NL]),
    ],
  },
  seller_bank_account_iban: {
    defaultValue: () => '',
    visibilityConditions: [
      () => state.invoiceForm.seller_bank_account_show_iban || countryCodeIs(state, [COUNTRY_CODES.RS, COUNTRY_CODES.NL]),
    ],
  },
  seller_bank_account_swift: {
    defaultValue: () => '',
    visibilityConditions: [
      () => state.invoiceForm.seller_bank_account_show_iban || countryCodeIs(state, [COUNTRY_CODES.RS, COUNTRY_CODES.NL]),
    ],
  },
  seller_bank_account_sort_code: {
    defaultValue: () => '',
    visibilityConditions: [
      () => buyerAndSellerCountryIsEq(state.invoiceForm, COUNTRY_CODES.GB),
    ],
  },
  seller_bank_account_routing_number: {
    defaultValue: () => '',
    visibilityConditions: [
      () => buyerAndSellerCountryIsEq(state.invoiceForm, COUNTRY_CODES.EN),
    ],
  },
  seller_email: {
    validations: [
      { validator: multipleEmailsValidator },
    ],
    defaultValue: () => '',
  },
  seller_phone: {
    defaultValue: () => '',
    validations: [
      {
        validator: (rule, value, callback) => {
          if (!!value && !state.phoneValidations.seller && state.phoneManuallyEdited.seller) {
            callback(new Error('phone_is_not_valid'));
          } else {
            callback();
          }
        },
        trigger: 'blur',
      },
    ],
  },
  seller_web: {
    defaultValue: () => '',
  },
  note: {
    defaultValue: () => '',
  },
  foot_note: {
    defaultValue: () => '',
    visibilityConditions: [
      () => countryCodeIsNot(state, [COUNTRY_CODES.EN, COUNTRY_CODES.RS, COUNTRY_CODES.PL, COUNTRY_CODES.NL, COUNTRY_CODES.GB]),
    ],
  },
  lines: {
    defaultValue: () => defaultValues.lines,
  },
  show_qr_code: {
    defaultValue: () => true,
    visibilityConditions: [
      () => (includes(state.invoiceForm.means_of_payment, [
        BANK_TRANSFER,
        CREDIT_CARD,
      ]) && includes(COUNTRY_CODES[state.env.country.toUpperCase()], [
        COUNTRY_CODES.CS, COUNTRY_CODES.SK, COUNTRY_CODES.NL,
      ])),
    ],
  },
  register_eet: {
    defaultValue: () => true,
  },
  eet_invoice: {
    defaultValue: () => false,
  },
  recurring_ends_on: {
    validations: [
      {
        type: 'date',
        required: true,
        message: 'date_invalid',
      },
      {
        validator: (rule, value, callback) => {
          dateInFutureValidator(rule, value, state, callback);
        },
      },
    ],
  },
  recurring_ends_after_count: {
    validations: [
      {
        required: true,
        message: 'recurring_invoice_count_required',
      },
      {
        type: 'number',
        min: 1,
        message: 'recurring_invoice_count_minimum',
      },
    ],
  },
});

// TODO: add tests
// getEetFormConfig have state as a first parameter
export const getEetFormConfig = () => ({
  establishment_number: {
    value: '',
    validations: [
      {
        required: true,
        message: 'establishment_number',
        trigger: 'blur',
      },
    ],
  },
  cert_password: {
    value: '',
    validations: [
      {
        required: true,
        message: 'cert_password',
        trigger: 'blur',
      },
    ],
  },
  vat_number: {
    value: '',
    validations: [
      {
        required: true,
        message: 'vat_number',
        trigger: 'blur',
      },
    ],
  },
  testing_mode: {
    value: '',
  },
  cert_file: {
    value: '',
  },
  enabled: {
    value: '',
  },
  not_subject_to_eet: {
    value: '',
  },
});

export const getExpenseFormConfig = state => ({
  contactId: {
    value: '',
    validations: [
      {
        required: false, // TODO: change to true
        message: 'contact_required',
      },
      {
        type: 'integer',
        message: 'contact_required',
      },
    ],
  },
  number: {
    value: '',
    validations: [
      {
        required: true,
        message: 'expense_number_required',
        trigger: 'blur',
      },
    ],
  },
  issuedOn: {
    value: '',
    validations: [
      {
        required: true,
        message: 'date_required',
      },
    ],
  },
  dueOn: {
    value: '',
    validations: [
      {
        required: true,
        message: 'date_required',
      },
      {
        validator: (rule, value, callback) => {
          expenseDateValidator(rule, value, state, callback);
        },
      },
    ],
  },
  taxAmount: {
    value: '',
    validations: [
      {
        required: true,
        message: 'tax_amount_required',
      },
    ],
  },
  totalAmount: {
    value: '',
    validations: [
      {
        required: true,
        message: 'total_amount_required',
      },
    ],
  },

});

export const getContactFormConfig = form => ({
  name: {
    value: '',
    validations: [
      {
        required: true,
        message: 'contact.name_required',
        trigger: 'blur',
      },
    ],
  },
  company_number: {
    value: '',
  },
  email: {
    value: '',
    validations: [
      { validator: multipleEmailsValidator },
    ],
  },
  phone: {
    value: '',
    validations: [
      {
        validator: (rule, value, callback) => {
          if (!!value && !form.phoneIsValid) {
            callback(new Error('phone_is_not_valid'));
          } else {
            callback();
          }
        },
        trigger: 'blur',
      },
    ],
  },
});

export const getResetPasswordFormConfig = form => ({
  token: {
    value: '',
    validations: [
      {
        required: true,
        message: 'password.token_required',
        trigger: 'blur',
      },
    ],
  },
  password: {
    value: '',
    validations: [
      {
        required: true,
        message: 'password.password_required',
        trigger: 'blur',
      },
      {
        min: 5,
        message: 'password.too_short',
        trigger: 'blur',
      },
      {
        validator: (rule, value, callback) => {
          validatePass(rule, value, form, 'passwordConfirmation', callback);
        },
        trigger: 'blur',
      },
    ],
  },
  passwordConfirmation: {
    value: '',
    validations: [
      {
        required: true,
        message: 'password.password_confirmation_required',
        trigger: 'blur',
      },
      {
        min: 5,
        message: 'password.too_short',
        trigger: 'blur',
      },
      {
        validator: (rule, value, callback) => {
          validatePassConfirmation(rule, value, form, 'password', callback);
        },
        trigger: 'blur',
      },
    ],
  },
});

export const getEmailFormConfig = () => ({
  thankYouBody: {
    validations: [
      {
        required: true,
        message: 'password.password_confirmation_required',
        trigger: 'blur',
      },
    ],
  },
  thankYouSubject: {
    validations: [
      {
        required: true,
        message: 'subject_required',
        trigger: 'blur',
      },
    ],
  },
  documentBody: {
    validations: [
      {
        required: true,
        message: 'password.password_confirmation_required',
        trigger: 'blur',
      },
    ],
  },
  documentSubject: {
    validations: [
      {
        required: true,
        message: 'subject_required',
        trigger: 'blur',
      },
    ],
  },
  reminderBody: {
    validations: [
      {
        required: true,
        message: 'password.password_confirmation_required',
        trigger: 'blur',
      },
    ],
  },
  reminderSubject: {
    validations: [
      {
        required: true,
        message: 'subject_required',
        trigger: 'blur',
      },
    ],
  },
});

export const getPasswordFormConfig = form => ({
  password: {
    value: '',
    validations: [
      {
        required: true,
        message: 'password.password_required',
        trigger: 'blur',
      },
    ],
  },
  currentPassword: {
    value: '',
    validations: [
      {
        required: true,
        message: 'password.current_password_required',
        trigger: 'blur',
      },
      {
        min: 5,
        message: 'password.too_short',
        trigger: 'blur',
      },
      {
        validator: (rule, value, callback) => {
          validatePass(rule, value, form, 'passwordConfirmation', callback);
        },
        trigger: 'blur',
      },
    ],
  },
  passwordConfirmation: {
    value: '',
    validations: [
      {
        required: true,
        message: 'password.password_confirmation_required',
        trigger: 'blur',
      },
      {
        min: 5,
        message: 'password.too_short',
        trigger: 'blur',
      },
      {
        validator: (rule, value, callback) => {
          validatePassConfirmation(rule, value, form, 'password', callback);
        },
        trigger: 'blur',
      },
    ],
  },
});

export const getSubmitInvoiceForm = () => ({
  senderEmail: {
    value: '',
    validations: [
      {
        required: true,
        message: 'email_required',
      },
      {
        type: 'email',
        message: 'email_format',
      },
    ],
  },
  recipientEmail: {
    value: '',
    validations: [
      {
        required: true,
        message: 'email_required',
      },
      { validator: multipleEmailsValidator },
    ],
  },
  subject: {
    value: '',
    validations: [
      {
        required: true,
        message: 'subject_required',
      },
    ],
  },
  message: {
    value: '',
    validations: [
      {
        required: true,
        message: 'message_required',
      },
    ],
  },
});

export const getSequenceFormConfig = () => ({
  expenseSequenceFormat: {
    value: '',
    validations: [
      {
        required: true,
        message: 'expense_sequence_format_required',
      },
    ],
  },
  invoiceSequenceFormat: {
    value: '',
    validations: [
      {
        required: true,
        message: 'invoice_sequence_format_required',
      },
    ],
  },
  taxCorrectionSequenceFormat: {
    value: '',
    validations: [
      {
        required: true,
        message: 'tax_correction_sequence_format_required',
      },
    ],
  },
  expenseSequenceNumber: {
    value: '',
    validations: [
      {
        required: true,
        message: 'expense_sequence_number_required',
      },
      {
        type: 'integer',
        message: 'expense_sequence_number_invalid',
      },
    ],
  },
  invoiceSequenceNumber: {
    value: '',
    validations: [
      {
        required: true,
        message: 'invoice_sequence_number_required',
      },
      {
        type: 'integer',
        message: 'invoice_sequence_number_invalid',
      },
    ],
  },
  taxCorrectionSequenceNumber: {
    value: '',
    validations: [
      {
        required: true,
        message: 'tax_correction_sequence_number_required',
      },
      {
        type: 'integer',
        message: 'tax_correction_sequence_number_invalid',
      },
    ],
  },
});
export const getSettingsFormConfig = () => ({});

export const getFormValidations = (formConfig) => {
  const valObj = {};
  Object.entries(formConfig).forEach(([key, value]) => {
    if (value.validations) {
      valObj[key] = value.validations;
    } else {
      valObj[key] = null;
    }
  });

  return valObj;
};

export const retrieveFormConfig = (state, form) => {
  switch (form) {
    case 'expenseForm':
      return getExpenseFormConfig(state);
    case 'eetForm':
      return getEetFormConfig(state);
    default:
      return getInvoiceFormConfig(state);
  }
};

export const getFormVisibilities = (state, form) => {
  const config = retrieveFormConfig(state, form);

  return map(
    value => (value.visibilityConditions
      ? value.visibilityConditions.every(rule => rule(state))
      : true),
    config,
  );
};

export const getFieldsDisabled = (state, form) => {
  const config = retrieveFormConfig(state, form);
  return map(
    value => (value.disableConditions
      ? value.disableConditions.every(rule => rule(state))
      : false),
    config,
  );
};

export const getDefaultValues = (form) => {
  const config = retrieveFormConfig(null, form);

  const values = map(
    value => (value.defaultValue ? value.defaultValue() : null),
    config,
  );

  return values;
};

export const getDefaultValue = (state, form, fieldName) => {
  if (!is(Object, state)) return undefined;

  const config = retrieveFormConfig(state, form);
  if (!hasPath([fieldName, 'defaultValue'], config)) return undefined;

  return config[fieldName].defaultValue();
};
